/**
 * As seções da Configurações, já resolvidas para QUEM está olhando.
 *
 * ── Por que isto é um arquivo separado
 *
 * A moldura (`ConfiguracoesPage`) e o painel (`SecaoIndice`) precisam da MESMA
 * lista de seções filtrada por permissão. Duplicar o cálculo abriria a chance de
 * os dois discordarem — o menu mostrando uma seção que o painel considera
 * inexistente.
 *
 * Mora fora do `.tsx` porque hook e helper convivendo com componente quebram o
 * fast refresh (o lint acusa, e com razão: o arquivo passa a ter dois motivos
 * para recarregar).
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { useMeusAcessos } from '@/lib/acesso-store';
import { useMyFeatureFlags } from '@/lib/feature-flags-store';
import { SECOES_CONFIG, filtrarPorFlag, filtrarPorPermissao, type SecaoConfig } from './catalogo';

/**
 * As seções que esta pessoa pode ver, já sem os itens que ela não alcança.
 *
 * Seção que fica sem nenhum item **some** — melhor que uma seção vazia que
 * promete e não entrega.
 */
export function useSecoesPermitidas(): SecaoConfig[] {
  const { user } = useAuth();
  const { itens: meusAcessos } = useMeusAcessos();
  const isAdmin = !!user?.isAdmin;
  const chaves = useMemo(
    () => new Set(meusAcessos.filter((a) => a.permitido).map((a) => a.chave)),
    [meusAcessos],
  );
  // As flags de módulo entram AQUI, no mesmo lugar da permissão, porque o menu e
  // o painel precisam enxergar a mesma lista — dois cálculos abririam a chance
  // de o menu mostrar um item que o painel considera inexistente.
  const { data: flagsData } = useMyFeatureFlags();
  const flags = flagsData?.flags;
  return useMemo(
    () =>
      SECOES_CONFIG.map((s) => ({
        ...s,
        itens: filtrarPorFlag(filtrarPorPermissao(s.itens, { isAdmin, chaves }), flags ?? {}),
      })).filter((s) => s.itens.length > 0),
    [isAdmin, chaves, flags],
  );
}

/** A seção que a URL pede; a primeira permitida quando a URL não diz (ou mente). */
export function useSecaoAtual(): SecaoConfig | undefined {
  const { secao } = useParams<{ secao?: string }>();
  const secoes = useSecoesPermitidas();
  return secoes.find((s) => s.id === secao) ?? secoes[0];
}

/**
 * Carimba `?de=<secao>` no destino — é o que permite a tela de chegada oferecer
 * caminho de volta (G-3 da rodada anterior).
 *
 * Preserva a query que o destino já tem: vários itens apontam pra `?aba=`, e
 * concatenar cru geraria `?aba=x?de=y`.
 */
export function comOrigem(para: string, secaoId: string): string {
  // Destino que já mora DENTRO da casca não precisa de `?de=`: o caminho de
  // volta é o próprio menu, sempre visível ao lado. Carimbar aqui fazia a barra
  // "← Voltar para Configurações" aparecer DENTRO da Configurações — um convite
  // a sair de onde a pessoa já está.
  //
  // ⚠️ Não basta olhar o prefixo `/admin/configuracoes/`: `/geral` e
  // `/horario-funcionamento` moram sob ele e estão FORA da casca (são telas
  // próprias, do caso 2 do K-3). Elas precisam do `?de=` até migrarem. O que
  // define "dentro" é o primeiro segmento ser uma SEÇÃO conhecida.
  const dentro = /^\/admin\/configuracoes\/([^/]+)(?:\/|$)/.exec(para);
  if (dentro && SECOES_CONFIG.some((s) => s.id === dentro[1])) return para;
  const [caminho, hash] = para.split('#');
  const [base, query] = caminho.split('?');
  const q = new URLSearchParams(query ?? '');
  q.set('de', secaoId);
  return `${base}?${q.toString()}${hash ? `#${hash}` : ''}`;
}

/**
 * O texto do "?" de cada seção. Diz o que se resolve ali e o que NÃO se resolve
 * — a segunda metade é a que evita o passeio pelas seis seções.
 */
export function ajudaDaSecao(id: string): string {
  switch (id) {
    case 'pessoas':
      return 'Quem entra no sistema e o que cada pessoa consegue abrir. Para MUDAR o que alguém acessa, use "Quem pode abrir o quê" — é lá que perfil, departamento e exceção se somam.';
    case 'catalogos':
      return 'As listas que aparecem nos campos de seleção do sistema (tipos, motivos, origens, tags). Se um campo de escolha está sem a opção que você precisa, ela nasce aqui.';
    case 'comercial':
      return 'Como o funil, as propostas e os lembretes se comportam. As metas por vendedor também ficam aqui; os indicadores do time ficam em Comercial › Indicadores.';
    case 'financeiro':
      return 'Impostos, contas bancárias e o que o sistema trata como financiamento em vez de despesa. Mexer aqui muda o cálculo de margem de todo mundo.';
    case 'sistema':
      return 'Expediente, feriados, avisos e o que está ligado. O horário de funcionamento é a base do cálculo de prazo — mudar aqui muda todos os prazos do sistema.';
    case 'integracoes':
      return 'O que o sistema troca com outros sistemas. A sincronização do Omie é somente leitura: nada que você fizer aqui escreve no Omie.';
    default:
      return 'Configurações desta área.';
  }
}
